import clsx from 'clsx';
import { Activity, BellRing, BookOpen, Bot, ChevronRight, Clock, Columns3, FileSearch, FileStack, GanttChart, FolderKanban, Inbox, Layers, LayoutDashboard, ListTodo, LogOut, Menu, Plus, Rocket, Search, Settings2, Tags, Timer, UserCog, Users, X } from 'lucide-react';
import { createContext, useContext, useEffect, useId, useState, type ReactNode } from 'react';
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router';
import { useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api.ts';
import { useMe } from '../lib/auth.tsx';
import { Logo } from './Logo.tsx';
import { NewTaskModal } from './NewTaskModal.tsx';
import { ActiveTimerPill } from './time.tsx';
import { NotificationBell } from './Notifications.tsx';

export type NewTaskDefaults = { projectKey?: string; status?: string; storyKey?: string; epicKey?: string; type?: string };
const NewTaskContext = createContext<(defaults?: NewTaskDefaults) => void>(() => {});
export const useNewTask = () => useContext(NewTaskContext);

function NavItem({ to, icon, children, onClick }: { to: string; icon: ReactNode; children: ReactNode; onClick?: () => void }) {
  return (
    <NavLink
      to={to}
      end={to === '/'}
      onClick={onClick}
      className={({ isActive }) =>
        clsx(
          'flex items-center gap-2.5 rounded-md px-2.5 py-1.5 text-sm transition-colors',
          isActive ? 'bg-white/10 font-medium text-white' : 'text-navy-200 hover:bg-white/5 hover:text-white',
        )
      }
    >
      <span className="[&>svg]:size-4">{icon}</span>
      {children}
    </NavLink>
  );
}

type NavEntry = { to: string; icon: ReactNode; label: string; adminOnly?: boolean };
type NavGroup = { id: string; title: string; items: NavEntry[] };

/** Menu lateral: itens soltos no topo e o resto em categorias recolhíveis. */
const TOP: NavEntry[] = [
  { to: '/', icon: <LayoutDashboard />, label: 'Dashboard' },
  { to: '/board', icon: <Columns3 />, label: 'Board' },
  { to: '/backlog', icon: <Inbox />, label: 'Backlog' },
];
const GROUPS: NavGroup[] = [
  {
    id: 'planning',
    title: 'Planejamento',
    items: [
      { to: '/projects', icon: <FolderKanban />, label: 'Projetos' },
      { to: '/docs', icon: <BookOpen />, label: 'Documentos' },
      { to: '/roadmap', icon: <GanttChart />, label: 'Roadmap' },
      { to: '/stories', icon: <Layers />, label: 'Histórias' },
      { to: '/sprints', icon: <Timer />, label: 'Sprints' },
      { to: '/releases', icon: <Rocket />, label: 'Versões' },
    ],
  },
  {
    id: 'tasks',
    title: 'Tarefas',
    items: [
      { to: '/tasks', icon: <ListTodo />, label: 'Tarefas' },
      { to: '/consultas', icon: <FileSearch />, label: 'Consultas' },
      { to: '/templates', icon: <FileStack />, label: 'Modelos' },
      { to: '/labels', icon: <Tags />, label: 'Etiquetas' },
    ],
  },
  {
    id: 'reports',
    title: 'Relatórios',
    items: [
      { to: '/reports/time', icon: <Clock />, label: 'Relatório de tempo' },
      { to: '/reports/agents', icon: <Bot />, label: 'Painel de agentes' },
      { to: '/activity', icon: <Activity />, label: 'Atividade' },
    ],
  },
  {
    id: 'admin',
    title: 'Administração',
    items: [
      { to: '/admin/agents', icon: <Bot />, label: 'Agentes' },
      { to: '/admin/users', icon: <Users />, label: 'Usuários' },
      { to: '/admin/board', icon: <Settings2 />, label: 'Colunas do board', adminOnly: true },
    ],
  },
];
const NAV_KEY = 'jynexus.nav.open';
const groupOf = (path: string) => GROUPS.find((g) => g.items.some((i) => path === i.to || path.startsWith(`${i.to}/`)))?.id;

function loadOpen(): Record<string, boolean> {
  try {
    const v = JSON.parse(localStorage.getItem(NAV_KEY) ?? 'null');
    if (v && typeof v === 'object') return v;
  } catch {
    /* sem armazenamento: usa o padrão */
  }
  return { planning: true, tasks: true };
}

function NavSection({ group, open, active, onToggle, children }: { group: NavGroup; open: boolean; active: boolean; onToggle: () => void; children: ReactNode }) {
  const id = `nav-${group.id}-${useId()}`;
  return (
    <div className="mt-3">
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={open}
        aria-controls={id}
        className={clsx(
          'flex w-full items-center gap-1.5 rounded-md px-2.5 py-1 text-[11px] font-semibold tracking-wider uppercase transition-colors hover:bg-white/5 hover:text-white',
          active && !open ? 'text-white' : 'text-navy-400',
        )}
      >
        {group.title}
        {active && !open && <span className="size-1.5 rounded-full bg-accent-500" aria-hidden />}
        <ChevronRight className={clsx('ml-auto size-3.5 transition-transform duration-200', open && 'rotate-90')} aria-hidden />
      </button>
      <div id={id} role="group" aria-label={group.title} inert={!open} className={clsx('grid transition-[grid-template-rows,visibility] duration-200 ease-out', open ? 'visible grid-rows-[1fr]' : 'invisible grid-rows-[0fr]')}>
        <div className="flex flex-col gap-0.5 overflow-hidden pt-0.5">{children}</div>
      </div>
    </div>
  );
}

export function Layout() {
  const me = useMe();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [newTask, setNewTask] = useState<NewTaskDefaults | null>(null);
  const [search, setSearch] = useState('');
  const close = () => setMobileOpen(false);
  const { pathname } = useLocation();
  const activeGroup = groupOf(pathname);
  const [open, setOpen] = useState<Record<string, boolean>>(loadOpen);
  const setGroup = (id: string, value: boolean) =>
    setOpen((prev) => {
      const next = { ...prev, [id]: value };
      try {
        localStorage.setItem(NAV_KEY, JSON.stringify(next));
      } catch {
        /* sem armazenamento: só nesta aba */
      }
      return next;
    });
  // A categoria da página atual sempre aparece aberta.
  useEffect(() => {
    if (activeGroup) setGroup(activeGroup, true);
  }, [activeGroup]);

  const logout = async () => {
    await api.logout().catch(() => {});
    qc.clear();
    navigate('/login');
  };

  const sidebar = (
    <nav className="flex h-full flex-col gap-1 p-3" aria-label="Navegação principal">
      <div className="mb-5 px-2 pt-1">
        <Logo size={32} />
      </div>
      {TOP.map((i) => (
        <NavItem key={i.to} to={i.to} icon={i.icon} onClick={close}>{i.label}</NavItem>
      ))}
      <div className="-mx-1 min-h-0 flex-1 overflow-y-auto px-1 pb-3">
        {GROUPS.map((g) => (
          <NavSection key={g.id} group={g} open={!!open[g.id]} active={activeGroup === g.id} onToggle={() => setGroup(g.id, !open[g.id])}>
            {g.items
              .filter((i) => !i.adminOnly || me.role === 'admin')
              .map((i) => (
                <NavItem key={i.to} to={i.to} icon={i.icon} onClick={close}>{i.label}</NavItem>
              ))}
          </NavSection>
        ))}
      </div>
      <div className="border-t border-white/10 pt-3">
        <NavItem to="/settings/notifications" icon={<BellRing />} onClick={close}>Notificações</NavItem>
        <NavItem to="/account" icon={<UserCog />} onClick={close}>
          <span className="truncate">{me.name}</span>
        </NavItem>
        <button onClick={logout} className="flex w-full items-center gap-2.5 rounded-md px-2.5 py-1.5 text-sm text-navy-200 hover:bg-white/5 hover:text-white">
          <LogOut className="size-4" /> Sair
        </button>
      </div>
    </nav>
  );

  return (
    <NewTaskContext.Provider value={(d) => setNewTask(d ?? {})}>
      <div className="flex h-full">
        <aside className="hidden w-56 shrink-0 bg-navy-900 lg:block">{sidebar}</aside>
        {mobileOpen && (
          <div className="fixed inset-0 z-40 lg:hidden">
            <div className="absolute inset-0 bg-navy-950/50" onClick={close} />
            <aside className="absolute inset-y-0 left-0 w-64 bg-navy-900">
              <button onClick={close} className="absolute top-3 right-3 text-navy-200" aria-label="Fechar menu">
                <X className="size-5" />
              </button>
              {sidebar}
            </aside>
          </div>
        )}
        <div className="flex min-w-0 flex-1 flex-col">
          <header className="flex h-14 shrink-0 items-center gap-2 border-b border-slate-200 bg-white px-3 sm:px-4">
            <button className="rounded p-1.5 text-slate-600 hover:bg-slate-100 lg:hidden" onClick={() => setMobileOpen(true)} aria-label="Abrir menu">
              <Menu className="size-5" />
            </button>
            <form
              className="relative max-w-md flex-1"
              onSubmit={(e) => {
                e.preventDefault();
                const q = search.trim();
                if (!q) return;
                if (/^jyn-\d+$/i.test(q)) navigate(`/tasks/${q.toUpperCase()}`);
                else navigate(`/tasks?q=${encodeURIComponent(q)}&includeClosed=true`);
              }}
            >
              <Search className="pointer-events-none absolute top-1/2 left-2.5 size-4 -translate-y-1/2 text-slate-400" />
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Buscar tarefa ou JYN-123"
                aria-label="Buscar tarefas"
                className="h-9 w-full rounded-md border border-slate-200 bg-slate-50 pr-3 pl-8 text-sm placeholder:text-slate-400 focus:border-navy-400 focus:bg-white focus:outline-none"
              />
            </form>
            <div className="hidden flex-1 sm:block" />
            <ActiveTimerPill />
            <NotificationBell />
            <button
              onClick={() => setNewTask({})}
              className="inline-flex h-9 items-center gap-1.5 rounded-md bg-accent-500 px-3 text-sm font-medium text-white hover:bg-accent-600"
            >
              <Plus className="size-4" /> <span className="hidden sm:inline">Nova tarefa</span>
            </button>
          </header>
          <main className="relative min-h-0 flex-1 overflow-y-auto">
            <Outlet />
          </main>
        </div>
      </div>
      {newTask && <NewTaskModal defaults={newTask} onClose={() => setNewTask(null)} />}
    </NewTaskContext.Provider>
  );
}

export function PageHeader({ title, subtitle, actions }: { title: ReactNode; subtitle?: ReactNode; actions?: ReactNode }) {
  return (
    <div className="flex flex-wrap items-end justify-between gap-3 px-4 pt-5 pb-4 sm:px-6">
      <div className="min-w-0">
        <h1 className="text-xl font-semibold tracking-tight text-slate-900">{title}</h1>
        {subtitle && <p className="mt-0.5 text-sm text-slate-500">{subtitle}</p>}
      </div>
      {actions && <div className="flex flex-wrap items-center gap-2">{actions}</div>}
    </div>
  );
}
